import {
    type Dispatch, type SetStateAction, type ReactNode,
    createContext, useMemo, useState,
} from 'react';
import { useCircularIterator } from 'palette/hooks/useCircularIterator';
import useInterval from 'palette/hooks/useInterval';
import { PromobarSlideItem } from 'palette/PromoBarV2/types';
import { Banner } from '../types';

interface Context {
    activeSlide: PromobarSlideItem | null;
    activeBanner: Banner | null;
    isExpanded: boolean;
    setIsExpanded(value: boolean): void;
    startInterval(): void;
    stopInterval(): void;
    previousBanner(): void;
    nextBanner(): void;
    setIsLanguageSelectorActive: Dispatch<SetStateAction<boolean>>;
    isLanguageSelectorActive: boolean;
    hasMultipleBanners: boolean;
}

interface ProviderProps {
    children: ReactNode;
    defaultId: number;
    banners: Banner[];
    rotationInterval?: number;
    promoBarSlides: PromobarSlideItem[] | null;
}

export const HeaderTopContext = createContext<Context | undefined>(undefined);
HeaderTopContext.displayName = 'HeaderTopContext';

export function HeaderTopContextProvider({
    defaultId,
    banners,
    promoBarSlides,
    rotationInterval = 4000,
    children,
}: ProviderProps) {
    const isPromoBarV2 = !!promoBarSlides;
    const hasMultipleBanners = isPromoBarV2 ? promoBarSlides.length > 1 : banners.length > 1;
    const [isLanguageSelectorActive, setIsLanguageSelectorActive] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeId, previousBanner, nextBanner] = useCircularIterator(
        isPromoBarV2 ? promoBarSlides.length : banners.length,
        isPromoBarV2 ? 0 : defaultId
    );
    const { startInterval, stopInterval } = useInterval(nextBanner, rotationInterval, hasMultipleBanners);
    const activeBanner = banners?.[activeId] || null;

    // Active Slide is V2 of promobar (new)
    const activeSlide = promoBarSlides?.[activeId] || null;

    const contextValue = useMemo(() => ({
        activeSlide,
        activeBanner,
        isExpanded,
        setIsExpanded,
        startInterval,
        stopInterval,
        previousBanner,
        nextBanner,
        setIsLanguageSelectorActive,
        isLanguageSelectorActive,
        hasMultipleBanners,
    }), [
        activeSlide, activeBanner, hasMultipleBanners, isExpanded, isLanguageSelectorActive, nextBanner,
        previousBanner, startInterval, stopInterval]);

    return (
        <HeaderTopContext.Provider value={contextValue}>
            {children}
        </HeaderTopContext.Provider>
    );
}
